import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, X, FileVideo, Loader2, CheckCircle2 } from 'lucide-react';
import { trainingAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

const UploadClipForm = ({ sessionId, onSuccess, onCancel }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [currentUploadIndex, setCurrentUploadIndex] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [formData, setFormData] = useState({
        down: '',
        distance: '',
        formation: '',
        personnel: '',
        play_type: '',
        result: ''
    });

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('video/'));

            if (files.length > 0) {
                // Add new files to existing selection
                setSelectedFiles(prev => [...prev, ...files]);
            } else {
                toast({ title: 'Please select video files', variant: 'destructive' });
            }
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files).filter(file => file.type.startsWith('video/'));
            if (files.length > 0) {
                setSelectedFiles(prev => [...prev, ...files]);
            } else {
                toast({ title: 'Please select video files', variant: 'destructive' });
            }
        }
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            toast({ title: 'Please select at least one file', variant: 'destructive' });
            return;
        }

        setUploading(true);
        setCurrentUploadIndex(0);
        setUploadProgress(0);

        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            setCurrentUploadIndex(i);
            setUploadProgress(0);

            try {
                const uploadFormData = new FormData();
                uploadFormData.append('file', file);
                uploadFormData.append('title', file.name.replace(/\.[^/.]+$/, '')); // Use filename as title

                if (formData.down) uploadFormData.append('down', formData.down);
                if (formData.distance) uploadFormData.append('distance', formData.distance);
                if (formData.formation) uploadFormData.append('formation', formData.formation);
                if (formData.personnel) uploadFormData.append('personnel', formData.personnel);
                if (formData.play_type) uploadFormData.append('play_type', formData.play_type);
                if (formData.result) uploadFormData.append('result', formData.result);
                uploadFormData.append('order_index', '0');

                // Simulate progress
                const progressInterval = setInterval(() => {
                    setUploadProgress(prev => Math.min(prev + 10, 90));
                }, 200);

                await trainingAPI.uploadVideo(sessionId, uploadFormData);

                clearInterval(progressInterval);
                setUploadProgress(100);
                successCount++;
            } catch (error) {
                console.error(`Upload failed for ${file.name}:`, error);
                failCount++;
                toast({
                    title: `Failed to upload ${file.name}`,
                    description: error.message || 'An error occurred',
                    variant: 'destructive'
                });
            }

            // Briefly pause between uploads to show completion state
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        setUploading(false);
        setCurrentUploadIndex(null);

        if (successCount > 0) {
            toast({
                title: 'Upload Complete',
                description: `Successfully uploaded ${successCount} videos.${failCount > 0 ? ` ${failCount} failed.` : ''}`
            });
            onSuccess();
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    };

    return (
        <div className="space-y-4 py-4">
            {/* File Upload Area */}
            {selectedFiles.length === 0 ? (
                <div
                    className={`border-2 border-dashed rounded-2xl p-8 sm:p-12 text-center transition-all duration-300 ${dragActive
                        ? 'border-blue-500 bg-blue-500/20 scale-[1.02] shadow-[0_0_30px_rgba(59,130,246,0.2)]'
                        : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'
                        }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <div className="bg-blue-500/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                        <Upload className="w-10 h-10 text-blue-400" />
                    </div>
                    <p className="text-gray-200 font-medium text-lg mb-2">
                        <span className="hidden sm:block">Drag and drop your videos here</span>
                        <span className="sm:hidden block">Select your videos to upload</span>
                    </p>
                    <p className="text-sm text-gray-500 mb-6">Support for multiple file upload</p>
                    <input
                        id="file-upload"
                        type="file"
                        accept="video/*"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    <label htmlFor="file-upload">
                        <Button type="button" asChild className="bg-blue-600 hover:bg-blue-500 text-white rounded-xl h-11 px-6 shadow-lg shadow-blue-900/20 cursor-pointer">
                            <span>Browse Files</span>
                        </Button>
                    </label>
                </div>
            ) : (
                <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar">
                    {selectedFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white/5 p-4 rounded-xl border border-white/5 hover:border-white/10 transition-colors">
                            <div className="flex items-center gap-4 overflow-hidden">
                                <div className="bg-[#121214] p-2.5 rounded-lg border border-white/5 flex-shrink-0">
                                    {currentUploadIndex > idx ? (
                                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                                    ) : currentUploadIndex === idx ? (
                                        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                                    ) : (
                                        <FileVideo className="w-6 h-6 text-blue-400" />
                                    )}
                                </div>
                                <div className="text-left min-w-0">
                                    <p className="text-gray-200 font-medium text-sm truncate">{file.name}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{formatFileSize(file.size)}</p>
                                </div>
                            </div>
                            {!uploading && (
                                <button
                                    onClick={() => removeFile(idx)}
                                    className="text-gray-500 hover:text-red-400 hover:bg-red-500/10 p-2 rounded-lg transition-colors ml-2"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                    ))}

                    {!uploading && (
                        <div className="text-right pt-2">
                            <input
                                id="add-more"
                                type="file"
                                accept="video/*"
                                multiple
                                onChange={handleFileSelect}
                                className="hidden"
                            />
                            <label htmlFor="add-more" className="inline-block py-2 px-4 rounded-xl text-sm font-medium text-blue-400 hover:bg-blue-500/10 hover:text-blue-300 transition-colors cursor-pointer border border-blue-500/20">
                                + Add more videos
                            </label>
                        </div>
                    )}
                </div>
            )}

            {/* Upload Progress Bar */}
            {uploading && (
                <div className="space-y-3 bg-white/5 p-4 rounded-xl border border-white/5 mt-4">
                    <div className="flex justify-between text-sm font-medium">
                        <span className="text-gray-300">
                            Uploading {currentUploadIndex + 1} of {selectedFiles.length}...
                        </span>
                        <span className="text-blue-400">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-[#121214] rounded-full h-2.5 shadow-inner overflow-hidden border border-white/5">
                        <div
                            className="bg-gradient-to-r from-blue-600 to-indigo-500 h-full rounded-full transition-all duration-300 shadow-[0_0_10px_rgba(59,130,246,0.6)]"
                            style={{ width: `${uploadProgress}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Shared Metadata Fields Removed as per request */}

            {/* Actions */}
            <div className="bg-[#121214]/90 backdrop-blur-md border-t border-gray-800/60 p-5 sm:p-6 sticky bottom-0 z-20 mt-4 -mx-5 sm:-mx-6 -mb-4 border-b-0">
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:justify-end">
                    <Button
                        variant="ghost"
                        onClick={onCancel}
                        className="w-full sm:w-auto text-gray-300 hover:text-white hover:bg-white/5 rounded-xl h-12 sm:h-11 font-medium order-2 sm:order-1"
                        disabled={uploading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpload}
                        disabled={selectedFiles.length === 0 || uploading}
                        className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white rounded-xl h-12 sm:h-11 font-medium shadow-lg shadow-blue-900/20 order-1 sm:order-2"
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="w-5 h-5 sm:w-4 sm:h-4 mr-2 animate-spin" />
                                Uploading
                            </>
                        ) : (
                            <>
                                <Upload className="w-5 h-5 sm:w-4 sm:h-4 mr-2" />
                                Upload {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default UploadClipForm;
